import { useEffect, useRef, useState } from 'react';
import type { UUID } from '../../domain/types';
import { springStep } from './treeRelax';

export interface Point {
  x: number;
  y: number;
}

/**
 * Where each circle is drawn *right now*. `targets` is where the spacing says
 * it should be (treeRelax.ts); this hook carries every circle there on a small
 * spring, so a growing circle visibly pushes its neighbours aside and, when it
 * settles back, they drift back into place — and switching arrangement
 * (generations ↔ by age) glides everyone to their new spot rather than
 * snapping. A circle that is new to the screen starts from its resting spot
 * (`rest`) and slides to its target, so the push is visible on arrival too.
 * Under reduced motion everything snaps.
 *
 * The animation runs only while something is still travelling, then stops: at
 * rest there is no per-frame work at all.
 */
export function useSettledPositions(rest: Map<UUID, Point>, targets: Map<UUID, Point>, reducedMotion: boolean): Map<UUID, Point> {
  // First paint: at the resting spots (the spring below carries everyone to their target), or straight there under reduced motion.
  const [positions, setPositions] = useState<Map<UUID, Point>>(() => (reducedMotion ? new Map(targets) : atRest(rest, targets)));
  const state = useRef(new Map<UUID, { x: number; y: number; vx: number; vy: number }>());
  const raf = useRef<number | null>(null);
  const latest = useRef(targets);
  latest.current = targets;

  useEffect(() => {
    const cur = state.current;
    // Forget people who left the screen; start newcomers at their resting spot.
    for (const id of [...cur.keys()]) if (!targets.has(id)) cur.delete(id);
    for (const [id, t] of targets) {
      if (!cur.has(id)) {
        const from = reducedMotion ? t : rest.get(id) ?? t;
        cur.set(id, { x: from.x, y: from.y, vx: 0, vy: 0 });
      }
    }
    if (reducedMotion) {
      for (const [id, t] of targets) cur.set(id, { x: t.x, y: t.y, vx: 0, vy: 0 });
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = null;
      setPositions(new Map(targets));
      return;
    }
    if (raf.current) return; // already running; it reads the latest targets each frame
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(64, now - last);
      last = now;
      const t = latest.current;
      let moving = false;
      const next = new Map<UUID, Point>();
      for (const [id, target] of t) {
        const s = cur.get(id) ?? { x: target.x, y: target.y, vx: 0, vy: 0 };
        const sx = springStep(s.x, s.vx, target.x, dt);
        const sy = springStep(s.y, s.vy, target.y, dt);
        const settled = Math.abs(sx.x - target.x) < 0.05 && Math.abs(sx.v) < 0.5 && Math.abs(sy.x - target.y) < 0.05 && Math.abs(sy.v) < 0.5;
        const p = settled ? { x: target.x, y: target.y, vx: 0, vy: 0 } : { x: sx.x, y: sy.x, vx: sx.v, vy: sy.v };
        cur.set(id, p);
        if (!settled) moving = true;
        next.set(id, { x: p.x, y: p.y });
      }
      setPositions(next);
      raf.current = moving ? requestAnimationFrame(tick) : null;
    };
    raf.current = requestAnimationFrame(tick);
  }, [rest, targets, reducedMotion]);

  useEffect(
    () => () => {
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = null;
    },
    [],
  );

  return positions;
}

function atRest(rest: Map<UUID, Point>, targets: Map<UUID, Point>): Map<UUID, Point> {
  const out = new Map<UUID, Point>();
  for (const [id, t] of targets) out.set(id, rest.get(id) ?? t);
  return out;
}
